<template>
  <div class="h-full w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
    <div class="h-full container mx-auto">
      <div class="px-8 flex flex-col h-full w-full py-8">
        <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
          <div class="flex items-center text-sm font-light">
            <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
            <span class="mx-2">/</span>
            <span>Events</span>
          </div>
          <div class="font-bold">Events</div>
        </div>

        <!-- Events Tabs -->
        <div class="mt-8">
          <!-- Tab Navigation -->
          <div class="bg-white rounded-2xl border shadow-lg mb-6">
            <div class="flex border-b">
              <button @click="activeTab = 'events'" :class="[
                'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 rounded-l-2xl',
                activeTab === 'events'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              ]">
                <div class="flex items-center justify-center">
                  <font-awesome-icon icon="calendar-alt" class="mr-2" />
                  Events List
                </div>
              </button>
              <button @click="activeTab = 'career-fair'" :class="[
                'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 rounded-r-2xl',
                activeTab === 'career-fair'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              ]">
                <div class="flex items-center justify-center">
                  <font-awesome-icon icon="chart-bar" class="mr-2" />
                  Career Fair Outcomes
                </div>
              </button>
            </div>
          </div>

          <!-- Tab Content -->
          <div class="tab-content">
            <!-- Events List Tab -->
            <div v-if="activeTab === 'events'"
              class="h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">
              <div class="flex justify-between flex-wrap gap-4">
                <h1 class="text-base md:text-lg font-semibold">Events List</h1>
                <div class="flex justify-start items-center flex-wrap gap-4">
                  <div v-if="role == 'superadmin'"
                    class="flex justify-start items-center flex-wrap gap-4">
                    <div class="w-full sm:w-auto border rounded-lg px-4 h-8 flex items-center">
                      <div class="text-sm mr-2">Entity:</div>
                      <select v-model="filterByEntity" class="outline-none text-sm flex-grow">
                        <option :value="null">All</option>
                        <option :value="item.id" v-for="(item, index) in filteredEntities" :key="index">{{
                          getEntityName(item.entity) }}
                        </option>
                      </select>
                    </div>
                    <div class="w-full sm:w-auto border rounded-lg px-4 h-8 flex items-center">
                      <div class="text-sm mr-2">Event Type:</div>
                      <select v-model="filterByEventType" class="outline-none text-sm flex-grow">
                        <option value="">All</option>
                        <option value="Normal">Normal</option>
                        <option value="Virtual Career Fair">Virtual Career Fair</option>
                      </select>
                    </div>
                    <div class="w-full sm:w-auto">
                      <search @on-search="onSearch" class-list="px-4 h-8"></search>
                    </div>
                  </div>
                  <div v-else class="w-full sm:w-auto">
                    <search @on-search="onSearch" class-list="px-4 h-8"></search>
                  </div>
                  <div v-if="hasPermission('event-create')" @click.stop="createModal = true"
                    class="flex items-center bg-ternary hover:bg-white border-ternary border px-4 py-2 rounded-lg cursor-pointer text-white hover:text-ternary">
                    <icon-plus class="w-4 h-4"></icon-plus>
                    <span class="text-xs ml-2">Post an Event</span>
                  </div>
                </div>
              </div>

              <div class="flex flex-col" v-if="$apollo.queries.events.loading">
                <table-shimmer :titleHeaders="[
                  '#',
                  'Title',
                  'Description',
                  'Thumbnail',
                  'Entity',
                  'Event Type',
                  'No.of Registrations',
                  'View Registrations',
                  'Actions',
                ]" :rows="first"></table-shimmer>
              </div>
              <div v-else-if="!$apollo.queries.events.loading && !hasItems"
                class="h-full flex justify-center items-center">
                <h1 class="text-center text-xl font-medium">No events found!</h1>
              </div>
              <div v-else class="flex flex-col">
                <div class="flex flex-col overflow-x-auto">
                  <div class="inline-block min-w-full py-2">
                    <div class="overflow-x-auto">
                      <table class="min-w-full text-left text-sm font-light">
                        <thead class="border-b font-medium dark:border-neutral-500">
                          <tr>
                            <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                              #
                            </th>
                            <th scope="col" class="px-6 py-2 text-gray-400">
                              Title
                            </th>
                            <th scope="col" class="px-6 py-2 text-gray-400">
                              Description
                            </th>
                            <th scope="col" class="px-6 py-2 text-gray-400">
                              Thumbnail
                            </th>
                            <th scope="col" class="px-6 py-2 text-gray-400">
                              Entity
                            </th>
                            <th scope="col" class="px-6 py-2 text-gray-400">
                              Event Type
                            </th>
                            <th scope="col" class="px-6 py-2 text-gray-400">
                              No.of Registrations
                            </th>
                            <th scope="col" class="px-6 py-2 text-gray-400">
                              View Registrations
                            </th>
                            <th scope="col" class="px-6 py-2 text-gray-400">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(item, index) in items" :key="index"
                            class="border-b border-dashed dark:border-neutral-500">
                            <td class="whitespace-nowrap px-6 py-2 font-medium">
                              {{ paginatorInfo.firstItem + index }}
                            </td>
                            <td class="whitespace-nowrap px-6 py-2">
                              <span @click.stop="goToEvent(item.slug)"
                                class="font-medium hover:text-blue-600 hover:underline cursor-pointer">{{ item.title
                                }}</span>
                            </td>
                            <td class="px-6 py-2">
                              <div class="truncate-3">{{ item.description }}</div>
                            </td>
                            <td class="px-6 py-2">
                              <img v-if="item.thumbnail" class="h-12 w-12 rounded-lg object-contain"
                                :src="getSrcFile(item.thumbnail)" alt="" />
                              <div v-else class="text-xs text-center">
                                No Thumbnail
                              </div>
                            </td>
                            <td class="px-6 py-2">
                              {{ getEntityName(item.entity.entity) }}
                            </td>
                            <td class="px-6 py-2 text-center">
                              {{ item.event_type }}
                            </td>
                            <td class="px-6 py-2 text-center">
                              {{ item.registration_count }}
                            </td>
                            <td class="px-6 py-2">
                              <div class="flex justify-center">
                                <div v-if="
                                  hasPermission('event-registration-index') &&
                                  item.registration_count
                                " @click.stop="
                                  viewApplications(item.token, item.title)
                                  "
                                  class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer">
                                  <icon-document-list
                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-document-list>
                                </div>
                              </div>
                            </td>
                            <td class="px-6 py-2">
                              <div class="flex">
                                <div v-if="hasPermission('event-show')" @click.stop="viewItem(item)"
                                  class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                                  <icon-eye
                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                                </div>
                                <div v-if="hasPermission('event-edit')" @click.stop="editItem(item)"
                                  class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                                  <icon-pen
                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-pen>
                                </div>
                                <div v-if="hasPermission('event-delete')" @click.stop="deleteItem(item.id)"
                                  class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                                  <icon-trash
                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-trash>
                                </div>
                                <div v-if="canResendEventEmail" @click.stop="openResendEmail(item)"
                                  class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                  title="Resend Email">
                                  <icon-envelope
                                    class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-envelope>
                                </div>
                                <div v-if="hasPermission('event-show')" @click.stop="viewItem(item)"
                                  class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                                  <rsd-copy-to-clipboard :text-to-copy="getUrl(item.slug)"></rsd-copy-to-clipboard>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div class="mt-4">
                  <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                </div>
              </div>
            </div>

            <!-- Career Fair Outcomes Tab -->
            <CareerFairOutcomes v-if="activeTab === 'career-fair'" />
          </div>
        </div>

        <transition name="fade" mode="out-in">
          <base-full-modal v-if="createModal" :title="`Create Event`" @close="closeModal">
            <create class="text-left" :entities="filteredEntities" @created="onCreated" @close="closeModal"></create>
          </base-full-modal>
        </transition>

        <transition name="fade" mode="out-in">
          <base-full-modal v-if="editModal" :title="`Edit Event - ${selectedItem.title}`" @close="closeModal">
            <edit class="text-left" @updated="onUpdated" @close="closeModal" :selected-item="selectedItem"
              :entities="filteredEntities"></edit>
          </base-full-modal>
        </transition>

        <transition name="fade" mode="out-in">
          <base-full-modal v-if="isViewItem" :title="`View Event - ${selectedItem.title}`" height=""
            @close="closeModal">
            <view-post :item="selectedItem" @close="closeModal"></view-post>
          </base-full-modal>
        </transition>

        <resend-event-email-modal
          v-if="resendEmailModal && selectedItem"
          :event-token="selectedItem.token"
          :event-title="selectedItem.title"
          @close="closeResendEmail"
          @done="onResendEmailDone"
        ></resend-event-email-modal>
      </div>
    </div>
    <div class="h-full flex justify-center items-center" v-if="isLoading">
      <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
    </div>
  </div>
</template>

<script>
import IconEye from "../../components/icons/IconEye.vue";
import IconPen from "../../components/icons/IconPen.vue";
import IconTrash from "../../components/icons/IconTrash.vue";
import IconPlus from "../../components/icons/IconPlus.vue";
import IconEnvelope from "../../components/icons/IconEnvelope.vue";
import IconDocumentList from "../../components/icons/IconDocumentList.vue";
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import TableShimmer from "../../components/shared/TableShimmer.vue";
import Create from "./create.vue";
import Edit from "./edit.vue";
import ViewPost from "./view.vue";
import Search from "../../components/shared/search.vue";
import RsdCopyToClipboard from "../../components/shared/RsdCopyToClipboard.vue";
import CareerFairOutcomes from "./components/CareerFairOutcomes.vue";
import ResendEventEmailModal from "./components/ResendEventEmailModal.vue";
export default {
  name: "post-event-index",
  components: {
    IconEye,
    IconPen,
    IconTrash,
    IconPlus,
    IconEnvelope,
    IconDocumentList,
    Pagination,
    TableShimmer,
    Create,
    Edit,
    ViewPost,
    Search,
    RsdCopyToClipboard,
    CareerFairOutcomes,
    ResendEventEmailModal,
  },
  data() {
    return {
      events: [],
      first: 10,
      page: 1,
      createModal: false,
      editModal: false,
      isViewItem: false,
      resendEmailModal: false,
      selectedItem: null,
      filterByEntity: null,
      filterByEventType: "",
      entities: [],
      isLoading: false,
      searchText: "",
      activeTab: 'events', // Default to Events List tab
    };
  },
  watch: {
    searchText() {
      this.page = 1;
    },
    events(newVal) {
      if (newVal.error) {
        if (newVal.error_code == 403) {
          this.$router.replace("/no-permission");
        } else if (newVal.error_code == 401) {
          this.$router.replace("/");
        } else {
          alert(newVal.message);
          this.$router.replace("/");
        }
      }
    },
    filterByEntity() {
      this.page = 1;
    },
    filterByEventType() {
      this.page = 1;
    },
  },
  computed: {
    items() {
      return this.events.data;
    },
    hasItems() {
      if (this.items && this.items.length > 0) {
        return true;
      } else {
        return false;
      }
    },
    paginatorInfo() {
      return this.events.paginatorInfo;
    },
    role() {
      return this.$store.getters.role;
    },
    filteredEntities() {
      return this.entities.data;
    },
    // Direct permission check — does NOT auto-allow superadmin
    canResendEventEmail() {
      const permissions = this.$store.getters.permissions;
      if (!permissions) {
        return false;
      }
      const permissionsArray = permissions.split(",");
      for (let i = 0; i < permissionsArray.length; i++) {
        if (permissionsArray[i].trim() === "event-resend-email") {
          return true;
        }
      }
      return false;
    },
  },
  apollo: {
    events() {
      return {
        query: gql`
          query GetAllEvents(
            $first: Int!
            $page: Int!
            $entity_id: Int
            $event_by_type: String
            $should_authenticate: Boolean
            $search: String
          ) {
            events(
              first: $first
              page: $page
              entity_id: $entity_id
              event_by_type: $event_by_type
              should_authenticate: $should_authenticate
              search: $search
            ) {
              error
              error_code
              message
              data {
                id
                token
                event_type
                title
                slug
                description
                content
                thumbnail
                start_date
                end_date
                location
                focal_point
                display_time
                display_day
                display_as_pop_up
                entity_id
                registration_open
                open_for_students
                open_for_entity
                registration_type
                display_message
                registration_link
                registration_count
                created_at
                entity {
                  entity
                }
                event_employers {
                  partner_id
                  partner {
                    en_company_name
                  }
                }
              }
              paginatorInfo {
                count
                currentPage
                firstItem
                hasMorePages
                lastItem
                lastPage
                perPage
                total
              }
            }
          }
        `,
        variables() {
          return {
            first: this.first,
            page: this.page,
            entity_id: parseInt(this.filterByEntity),
            event_by_type: this.filterByEventType,
            should_authenticate: true,
            search: this.searchText,
          };
        },
        fetchPolicy: "cache-and-network",
      };
    },
    entities() {
      return {
        query: gql`
          query GetEntityList {
            entities {
              data {
                id
                entity
              }
            }
          }
        `,
        fetchPolicy: "cache-and-network",
      };
    },
  },
  methods: {
    onSearch(searchText) {
      this.searchText = searchText;
    },
    setPage(page) {
      this.page = page;
    },
    closeModal() {
      this.createModal = false;
      this.editModal = false;
      this.isViewItem = false;
      this.resendEmailModal = false;
      this.selectedItem = null;
    },
    openResendEmail(item) {
      this.selectedItem = item;
      this.resendEmailModal = true;
    },
    closeResendEmail() {
      this.resendEmailModal = false;
      this.selectedItem = null;
    },
    onResendEmailDone(message) {
      this.resendEmailModal = false;
      this.selectedItem = null;
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
      Toast.fire({
        icon: "success",
        title: message || "Event emails queued successfully",
      });
    },
    showToast(message = "") {
      this.$apollo.queries.events.refetch();
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 1000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", this.$swal.stopTimer);
          toast.addEventListener("mouseleave", this.$swal.resumeTimer);
        },
      });
      Toast.fire({
        icon: "success",
        title: message,
      });
      this.closeModal();
    },
    onCreated() {
      this.showToast("Event Posted Successfully");
    },
    editItem(item) {
      this.selectedItem = item;
      this.editModal = true;
    },
    async deleteItem(id) {
      let error_msg = null;
      this.$swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            const DELETE_MUTATION = gql`
              mutation DeleteEvent($id: ID!) {
                deleteEvent(id: $id) {
                  error
                  message
                }
              }
            `;

            try {
              await this.$apollo
                .mutate({
                  mutation: DELETE_MUTATION,
                  variables: {
                    id: id,
                  },
                })
                .then((data) => {
                  if (data.data.deleteEvent.error) {
                    error_msg = data.data.deleteEvent.message;
                  }
                })
                .catch((error) => {
                  error_msg = error.message;
                });
            } catch (error) {
              error_msg = error.message || "Please try again.";
            }

            if (error_msg === null) {
              this.showToast("Event has been deleted.");
            } else {
              this.$swal
                .fire({
                  icon: "error",
                  title: "Oops...",
                  text: error_msg,
                })
                .then((result) => {
                  if (result.value || result.dismiss) {
                    error_msg = null;
                  }
                });
            }
          }
        });
    },
    onUpdated() {
      this.showToast("Event Updated Successfully");
    },
    hasPermission(crudName) {
      if (this.role == "superadmin") {
        return true;
      } else {
        const permissions = this.$store.getters.permissions;
        const permissionsArray = permissions.split(",");
        if (permissionsArray.includes(crudName)) {
          return true;
        } else {
          return false;
        }
      }
    },
    viewItem(item) {
      this.selectedItem = item;
      this.isViewItem = true;
    },
    getSrcFile(url) {
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    viewApplications(token, title) {
      localStorage.setItem("eventRegistrationTitle", title);
      this.$router.replace(`/post-event/registrations/${token}`);
    },
    goToEvent(slug) {
      const url = `#/events/${slug}`;
      window.open(url, "_blank");
    },
    getEntityName(entity) {
      if (entity == "ATHS") {
        return "ATS";
      } else {
        return entity;
      }
    },
    getUrl(slug) {
      return process.env.VUE_APP_FRONTEND_URL + 'events/' + slug;
    }
  },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.truncate-3 {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
}
</style>
